export const LEVEL_LABELS = { 1: 'L1', 2: 'L2', 3: 'L3', 4: 'L4' };

export const LEVEL_BADGE_VARIANT = { 1: 'bl', 2: 'pu', 3: 'gr', 4: 'am' };

export const VERSION_STATUS_LABEL = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

/** Build parent/child tree from flat org_process_hierarchy rows. */
export function buildTree(flatNodes) {
  const byId = new Map();
  const roots = [];
  for (const n of flatNodes || []) {
    byId.set(n.id, { ...n, children: [] });
  }
  for (const n of flatNodes || []) {
    const node = byId.get(n.id);
    if (n.parent_id && byId.has(n.parent_id)) {
      byId.get(n.parent_id).children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortChildren = (nodes) => {
    nodes.sort(
      (a, b) =>
        (a.order_index ?? 1) - (b.order_index ?? 1) ||
        String(a.code || '').localeCompare(String(b.code || ''))
    );
    nodes.forEach((c) => sortChildren(c.children));
  };
  sortChildren(roots);
  return roots;
}

export function flattenTree(nodes, acc = []) {
  for (const n of nodes || []) {
    acc.push(n);
    if (n.children?.length) flattenTree(n.children, acc);
  }
  return acc;
}

export function filterTree(nodes, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return nodes;
  const walk = (node) => {
    const childMatches = (node.children || []).map(walk).filter(Boolean);
    const selfMatch =
      node.name?.toLowerCase().includes(q) ||
      node.code?.toLowerCase().includes(q);
    if (selfMatch || childMatches.length) {
      return { ...node, children: childMatches };
    }
    return null;
  };
  return (nodes || []).map(walk).filter(Boolean);
}

/** Collect all descendant ids (not including self). */
export function descendantIds(node) {
  const ids = [];
  const walk = (n) => {
    for (const c of n.children || []) {
      ids.push(c.id);
      walk(c);
    }
  };
  walk(node);
  return ids;
}

/** Checkbox state for hierarchical scope: checked | partial | none */
export function scopeCheckState(node, inScopeSet) {
  const desc = flattenTree(node.children || []);
  if (!desc.length) {
    return inScopeSet.has(node.id) ? 'checked' : 'none';
  }
  const checkedCount = desc.filter((d) => inScopeSet.has(d.id)).length;
  if (checkedCount === 0) return 'none';
  if (checkedCount === desc.length && inScopeSet.has(node.id)) return 'checked';
  if (checkedCount > 0) return 'partial';
  return 'none';
}

export function findNodeInTree(nodes, id) {
  for (const n of nodes || []) {
    if (n.id === id) return n;
    const found = findNodeInTree(n.children, id);
    if (found) return found;
  }
  return null;
}

export function findL1Ancestor(tree, nodeId) {
  for (const l1 of tree || []) {
    if (l1.id === nodeId) return l1;
    const flat = flattenTree(l1.children || []);
    if (flat.some((n) => n.id === nodeId)) return l1;
  }
  return null;
}

/** Clone all hierarchy nodes from one taxonomy version into a new draft version. */
export async function cloneTaxonomyVersion(sb, { organisationId, sourceVersionId, versionLabel, profileId }) {
  const { data: newVer, error: verErr } = await sb
    .from('taxonomy_versions')
    .insert({
      organisation_id: organisationId,
      version_label: versionLabel,
      status: 'draft',
      created_by: profileId || null,
      notes: 'Cloned from prior version',
    })
    .select()
    .single();
  if (verErr) throw verErr;

  const { data: nodes, error: nodesErr } = await sb
    .from('org_process_hierarchy')
    .select('*')
    .eq('taxonomy_version_id', sourceVersionId)
    .eq('organisation_id', organisationId);
  if (nodesErr) throw nodesErr;
  if (!nodes?.length) return newVer;

  const idMap = new Map();
  const sorted = [...nodes].sort((a, b) => a.level - b.level);
  for (const n of sorted) {
    const { data: inserted, error: insErr } = await sb
      .from('org_process_hierarchy')
      .insert({
        organisation_id: organisationId,
        taxonomy_version_id: newVer.id,
        template_node_id: n.template_node_id,
        level: n.level,
        code: n.code,
        name: n.name,
        description: n.description,
        parent_id: n.parent_id ? idMap.get(n.parent_id) : null,
        order_index: n.order_index,
        is_cross_functional: n.is_cross_functional,
        is_custom: n.is_custom,
        platform_kb_ref: n.platform_kb_ref,
      })
      .select('id')
      .single();
    if (insErr) throw insErr;
    idMap.set(n.id, inserted.id);
  }
  return newVer;
}

export async function publishTaxonomyVersion(sb, { versionId, organisationId, profileId }) {
  await sb
    .from('taxonomy_versions')
    .update({ status: 'archived' })
    .eq('organisation_id', organisationId)
    .eq('status', 'published');

  const { error } = await sb
    .from('taxonomy_versions')
    .update({
      status: 'published',
      published_at: new Date().toISOString(),
      published_by: profileId || null,
    })
    .eq('id', versionId);
  if (error) throw error;
}

/**
 * Ensure the org has at least one taxonomy version and orphan hierarchy rows are linked.
 * Handles legacy data where org_process_hierarchy rows exist without taxonomy_version_id.
 */
export async function ensureOrgTaxonomyVersion(sb, { organisationId, profileId }) {
  if (!organisationId) return [];

  const { data: versions, error: verErr } = await sb
    .from('taxonomy_versions')
    .select('*')
    .eq('organisation_id', organisationId)
    .order('created_at', { ascending: false });
  if (verErr) throw verErr;

  if (versions?.length) {
    const anchor = versions.find((v) => v.status === 'published') || versions[0];
    const { error: linkErr } = await sb
      .from('org_process_hierarchy')
      .update({ taxonomy_version_id: anchor.id })
      .eq('organisation_id', organisationId)
      .is('taxonomy_version_id', null);
    if (linkErr) throw linkErr;
    return versions;
  }

  const { count, error: countErr } = await sb
    .from('org_process_hierarchy')
    .select('*', { count: 'exact', head: true })
    .eq('organisation_id', organisationId);
  if (countErr) throw countErr;
  if (!count) return [];

  const { data: newVer, error: insErr } = await sb
    .from('taxonomy_versions')
    .insert({
      organisation_id: organisationId,
      version_label: 'v1.0',
      status: 'published',
      published_at: new Date().toISOString(),
      published_by: profileId || null,
      notes: 'Auto-created from existing company taxonomy',
    })
    .select()
    .single();
  if (insErr) throw insErr;

  const { error: linkErr } = await sb
    .from('org_process_hierarchy')
    .update({ taxonomy_version_id: newVer.id })
    .eq('organisation_id', organisationId)
    .is('taxonomy_version_id', null);
  if (linkErr) throw linkErr;

  return [newVer];
}

/** Insert parsed Rex taxonomy nodes into a draft version (level order, parent_code links). */
export async function insertTaxonomyNodes(sb, {
  organisationId, versionId, nodes, profileId, replace = false,
}) {
  if (!nodes?.length) return 0;

  if (replace) {
    const { error: delErr } = await sb
      .from('org_process_hierarchy')
      .delete()
      .eq('organisation_id', organisationId)
      .eq('taxonomy_version_id', versionId);
    if (delErr) throw delErr;
  }

  const sorted = [...nodes].sort((a, b) => (a.level || 1) - (b.level || 1));
  const codeToId = new Map();

  for (const n of sorted) {
    const level = Number(n.level) || 1;
    const code = String(n.code || '').trim();
    const name = String(n.name || '').trim();
    if (!code || !name) continue;

    let parentId = null;
    if (n.parent_code) {
      parentId = codeToId.get(String(n.parent_code).trim()) || null;
    } else if (n.parent_id) {
      parentId = n.parent_id;
    }

    const siblings = sorted.filter((s) => {
      if (s.parent_code && n.parent_code) return s.parent_code === n.parent_code;
      return !s.parent_code && !n.parent_code && s.level === level;
    });
    const orderIndex = siblings.indexOf(n) + 1;

    const { data: inserted, error } = await sb
      .from('org_process_hierarchy')
      .insert({
        organisation_id: organisationId,
        taxonomy_version_id: versionId,
        parent_id: parentId,
        level,
        code,
        name,
        description: n.description?.trim() || null,
        platform_kb_ref: n.platform_kb_ref?.trim() || null,
        order_index: n.order_index ?? orderIndex,
        is_custom: true,
      })
      .select('id, code')
      .single();
    if (error) throw error;
    codeToId.set(code, inserted.id);
  }

  return codeToId.size;
}

export async function ensureDraftTaxonomyVersion(sb, {
  organisationId, sourceVersionId, profileId, versionLabel,
}) {
  const { data: existingDraft } = await sb
    .from('taxonomy_versions')
    .select('*')
    .eq('organisation_id', organisationId)
    .eq('status', 'draft')
    .maybeSingle();
  if (existingDraft) return existingDraft;

  return cloneTaxonomyVersion(sb, {
    organisationId,
    sourceVersionId,
    versionLabel: versionLabel || 'Rex draft',
    profileId,
  });
}
